/**
 * The in-app notification feed's domain model.
 *
 * Deliberately free of any GraphQL import so it can ship — and be tested —
 * before the backend schema exists. The feed hook maps the wire shape onto
 * these types; everything downstream (rows, icons, i18n, CTAs) speaks only
 * this vocabulary.
 */

import type { NotificationTarget } from "./target";

/**
 * Every notification type the client knows how to render richly.
 *
 * Mirrors NOTIFICATION_TYPES in the backend's notifications/registry.py, and the
 * wire values are camelCase to match it exactly. This array is the union's
 * source of truth: adding an entry here makes `tsc` demand a matching icon
 * (presentation.ts) and matching i18n keys (i18n-keys.ts), which is what stops a
 * type shipping half-wired.
 *
 * The backend may legitimately send a type this list does not have yet — the two
 * repos deploy independently. That is NOT an error: see `resolveNotificationType`.
 */
export const NOTIFICATION_TYPES = [
  /** Sunday pipeline-cleanup digest. */
  "weekStart",
  /** Weekday morning prep digest. */
  "weekdayMorning",
  /** Weekday end-of-day follow-ups digest. */
  "weekdayEndOfDay",
  /** First calendar sync finished after connecting an account. */
  "calendarSyncCompleted",
  /** The user's first meeting recorded through Coffee. */
  "firstMeeting",
  /** An AI follow-up email was drafted after a meeting. */
  "followUpDrafted",
  /** One or more deal suggestions were produced from a meeting. */
  "dealSuggestion",
  /** One or more task suggestions were produced from a meeting. */
  "taskSuggestion",

  // --- The alert list from "Coffee Notifications - Alert details".
  //
  // These sit ALONGSIDE the three digest types above rather than replacing them. The
  // digests are what coffee-slack emits today; retiring them before it splits its cron
  // would blank every live notification. The client knowing both is what lets the two
  // repos cross over in either order.
  //
  // The four Sunday checks and the two weekday-morning ones are the same conditions the
  // digests aggregate — the spec wants them as separate alerts so each CTA can land on the
  // specific thing it is about.

  /** Someone assigned a task to this user. */
  "assignedTask",
  /** Someone made this user the owner of a deal. */
  "assignedDealOwner",
  /** Sunday: deals whose close date has passed. */
  "closeDateInPast",
  /** Sunday: deals with no activity in the last two weeks. */
  "noActivity",
  /** Sunday: deals with no next steps recorded. */
  "noNextSteps",
  /** Sunday: deals with one or fewer linked contacts. */
  "fewContacts",
  /** Sunday: how the pipeline moved over the past week. */
  "weeklyPipelineChanges",
  /** Weekday morning: today's meeting briefings are ready. */
  "dailyBriefing",
  /** Weekday morning: tasks due today. */
  "actionItemsDue",
  /** A CRM integration (Salesforce, HubSpot, …) finished its first sync. */
  "crmSyncCompleted",

  // --- Job changes (SC-13586), from the backend's daily job-change sweep. Sent to every user
  // who recently emailed or met the contact.

  /** A contact's title changed in our data source and was updated (it was enrichment-owned). */
  "contactJobTitleChanged",
  /** A contact's title changed, but a user typed the current one — so we ask, never overwrite. */
  "contactTitleChangeSuggested",
  /** A contact may have moved company. Always a question; nothing changes until someone accepts. */
  "contactCompanyChanged",
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

const KNOWN_TYPES: ReadonlySet<string> = new Set(NOTIFICATION_TYPES);

/**
 * Narrow a wire value to a known type, or null when the backend is ahead of us.
 *
 * Returning null rather than throwing is the whole point: a backend-only type
 * must still render (neutral icon + the server's `fallbackTitle`) instead of
 * blanking the feed. Deploy order between the two repos is then a non-issue.
 */
export function resolveNotificationType(raw: unknown): NotificationType | null {
  return typeof raw === "string" && KNOWN_TYPES.has(raw) ? (raw as NotificationType) : null;
}

/** One row in the feed, after mapping from the wire shape. */
export interface FeedNotification {
  id: string;
  /**
   * Null when the backend sent a type this build does not know. Renderers must
   * handle null by falling back to `fallbackTitle` / `fallbackBody`.
   */
  type: NotificationType | null;
  /** ISO 8601 UTC. Never a pre-rendered relative string — that cannot be translated. */
  createdAt: string;
  /** ISO 8601 when read, null when unread. `unread` is derived, never stored twice. */
  readAt: string | null;
  /**
   * Interpolation values for the type's i18n keys, e.g. `{ count: 3, meetingTitle: "…" }`.
   * Rendered verbatim, so plain text only. Dates arrive as ISO strings and counts as
   * numbers, so the client can format and pluralise them per locale.
   */
  params: Record<string, string | number>;
  /** Where the row's CTA goes. `{ kind: "none" }` renders a row with no action. */
  target: NotificationTarget;
  /** Server-rendered English, shown only when `type` is null. */
  fallbackTitle: string;
  fallbackBody: string | null;
}

/** Read state is derived, so there is exactly one place it can be wrong. */
export function isUnread(n: Pick<FeedNotification, "readAt">): boolean {
  return n.readAt == null;
}
